import { LoggedUser } from './LoginModal';

export interface SystemUserRow {
  id: string;
  username: string;
  fullName: string;
  badgeNumber: string;
  role: 'DISPATCHER' | 'SUPERVISOR' | 'ADMIN';
  active: boolean;
  createdAt: string;
}

export class AdminConsoleModal {
  private element: HTMLElement | null = null;
  private apiBaseUrl: string;
  private users: SystemUserRow[] = [];
  private retentionPolicy: { retentionDays: number; autoPurgeEnabled: boolean } = { retentionDays: 90, autoPurgeEnabled: true };

  constructor(apiBaseUrl: string, _currentUser: LoggedUser) {
    this.apiBaseUrl = apiBaseUrl;
  }

  public async mount(container: HTMLElement): Promise<void> {
    this.element = document.createElement('div');
    this.element.className = 'modal-backdrop';
    this.element.style.cssText = `
      position: fixed; inset: 0; background: rgba(0,0,0,0.85);
      backdrop-filter: blur(10px); z-index: 10000; display: flex;
      align-items: center; justify-content: center; padding: 20px;
    `;

    this.element.innerHTML = `
      <div style="background: #0f172a; border: 1px solid #334155; border-radius: 14px; width: 100%; max-width: 980px; max-height: 90vh; display: flex; flex-direction: column; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.7); overflow: hidden; color: #f8fafc;">
        
        <!-- Header -->
        <div style="padding: 18px 24px; border-bottom: 1px solid #1e293b; display: flex; justify-content: space-between; align-items: center; background: #1e293b;">
          <div style="display:flex; align-items:center; gap:12px;">
            <div style="width:38px; height:38px; background:linear-gradient(135deg, #d97706, #b45309); border-radius:10px; display:flex; align-items:center; justify-content:center; font-weight:900; font-size:1.1rem; color:#fff;">
              ⚙️
            </div>
            <div>
              <h2 style="margin: 0; font-size: 1.25rem; font-weight: 800;">Panel de Administración del Sistema CAD / 911</h2>
              <div style="font-size: 0.78rem; color: #94a3b8;">
                Gestión Tecnológica • Cuentas de Acceso • Roles y Permisos • Parámetros de Almacenamiento
              </div>
            </div>
          </div>
          <button id="btn-admin-close" style="background:transparent; border:none; color:#94a3b8; font-size:1.5rem; cursor:pointer; padding:4px 8px;">✕</button>
        </div>

        <!-- Tabs de Navegación del Administrador -->
        <div style="display:flex; border-bottom:1px solid #1e293b; background:#0b1120; padding: 0 24px;">
          <button id="admin-tab-users" class="tab-btn active" style="padding:12px 18px; border:none; background:transparent; color:#38bdf8; font-weight:700; border-bottom:2px solid #38bdf8; cursor:pointer;">
            👥 Usuarios y Permisos
          </button>
          <button id="admin-tab-storage" class="tab-btn" style="padding:12px 18px; border:none; background:transparent; color:#94a3b8; font-weight:600; cursor:pointer;">
            🗄️ Políticas de Almacenamiento & Purga
          </button>
          <button id="admin-tab-system" class="tab-btn" style="padding:12px 18px; border:none; background:transparent; color:#94a3b8; font-weight:600; cursor:pointer;">
            🔌 Estado Tecnológico de Servicios
          </button>
        </div>

        <!-- Contenedor Principal con Scroll -->
        <div id="admin-content-area" style="padding: 24px; overflow-y: auto; flex: 1;">
          <!-- Cargando por defecto -->
          <div style="text-align:center; padding:40px; color:#94a3b8;">Cargando catálogo del sistema...</div>
        </div>
      </div>
    `;

    container.appendChild(this.element);
    this.setupListeners();
    await this.loadUsersTab();
  }

  private setupListeners(): void {
    if (!this.element) return;

    this.element.querySelector('#btn-admin-close')?.addEventListener('click', () => {
      this.close();
    });

    const tabUsers = this.element.querySelector('#admin-tab-users') as HTMLElement;
    const tabStorage = this.element.querySelector('#admin-tab-storage') as HTMLElement;
    const tabSystem = this.element.querySelector('#admin-tab-system') as HTMLElement;

    const setTabActive = (activeEl: HTMLElement) => {
      [tabUsers, tabStorage, tabSystem].forEach(el => {
        el.style.color = '#94a3b8';
        el.style.borderBottom = 'none';
      });
      activeEl.style.color = '#38bdf8';
      activeEl.style.borderBottom = '2px solid #38bdf8';
    };

    tabUsers.addEventListener('click', async () => {
      setTabActive(tabUsers);
      await this.loadUsersTab();
    });

    tabStorage.addEventListener('click', async () => {
      setTabActive(tabStorage);
      await this.loadStorageTab();
    });

    tabSystem.addEventListener('click', async () => {
      setTabActive(tabSystem);
      await this.loadSystemTab();
    });
  }

  private async loadUsersTab(): Promise<void> {
    const area = this.element?.querySelector('#admin-content-area');
    if (!area) return;

    area.innerHTML = `
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:16px;">
        <div>
          <h3 style="margin:0; font-size:1.05rem;">Cuentas Operativas Registradas</h3>
          <p style="margin:2px 0 0; font-size:0.8rem; color:#94a3b8;">
            Control de acceso a consolas: Paramédicos (despacho), Supervisores (expedientes) y Administradores.
          </p>
        </div>
        <button id="btn-create-user" style="background:#0284c7; color:#fff; border:none; padding:8px 14px; border-radius:6px; font-weight:700; font-size:0.82rem; cursor:pointer;">
          + CREAR NUEVO USUARIO
        </button>
      </div>

      <!-- Formulario Desplegable para Crear Usuario -->
      <div id="new-user-form-panel" style="display:none; background:#1e293b; border:1px solid #38bdf8; border-radius:10px; padding:16px; margin-bottom:18px;">
        <h4 style="margin:0 0 12px; font-size:0.95rem; color:#38bdf8;">Registrar Nueva Cuenta de Acceso</h4>
        <div style="display:grid; grid-template-columns: 1fr 1fr; gap:12px;">
          <div>
            <label style="font-size:0.75rem; color:#94a3b8;">Nombre de Usuario (Login)</label>
            <input type="text" id="new-user-username" class="form-input" placeholder="ej. paramedico2" style="width:100%; box-sizing:border-box;" />
          </div>
          <div>
            <label style="font-size:0.75rem; color:#94a3b8;">Nombre Completo</label>
            <input type="text" id="new-user-fullname" class="form-input" placeholder="ej. Juan Pérez G." style="width:100%; box-sizing:border-box;" />
          </div>
          <div>
            <label style="font-size:0.75rem; color:#94a3b8;">No. de Placa / Identificador Operativo</label>
            <input type="text" id="new-user-badge" class="form-input" placeholder="ej. PM-912" style="width:100%; box-sizing:border-box;" />
          </div>
          <div>
            <label style="font-size:0.75rem; color:#94a3b8;">Perfil / Rol en Plataforma</label>
            <select id="new-user-role" class="form-input" style="width:100%; box-sizing:border-box; background:#0f172a; color:#fff;">
              <option value="DISPATCHER">PARAMÉDICO (Despacho 911 en vivo únicamente)</option>
              <option value="SUPERVISOR">SUPERVISOR (Revisión de Casos, Video, Chat y GPS)</option>
              <option value="ADMIN">ADMINISTRADOR (Gestión tecnológica y usuarios)</option>
            </select>
          </div>
          <div>
            <label style="font-size:0.75rem; color:#94a3b8;">Contraseña Temporal</label>
            <input type="password" id="new-user-password" class="form-input" placeholder="••••••••" style="width:100%; box-sizing:border-box;" />
          </div>
        </div>
        <div style="display:flex; justify-content:flex-end; gap:8px; margin-top:14px;">
          <button id="btn-cancel-new-user" style="background:transparent; border:1px solid #475569; color:#cbd5e1; padding:6px 14px; border-radius:6px; cursor:pointer;">Cancelar</button>
          <button id="btn-save-new-user" style="background:#10b981; color:#fff; border:none; padding:6px 16px; border-radius:6px; font-weight:700; cursor:pointer;">Guardar Usuario</button>
        </div>
      </div>

      <!-- Tabla de Usuarios -->
      <div style="background:#0b1120; border:1px solid #1e293b; border-radius:8px; overflow:hidden;">
        <table style="width:100%; border-collapse:collapse; text-align:left; font-size:0.85rem;">
          <thead>
            <tr style="background:#1e293b; color:#94a3b8; font-size:0.75rem; text-transform:uppercase;">
              <th style="padding:10px 14px;">Usuario</th>
              <th style="padding:10px 14px;">Nombre</th>
              <th style="padding:10px 14px;">Placa</th>
              <th style="padding:10px 14px;">Rol Asignado</th>
              <th style="padding:10px 14px;">Estado</th>
              <th style="padding:10px 14px; text-align:right;">Acciones</th>
            </tr>
          </thead>
          <tbody id="admin-users-table-body">
            <tr><td colspan="6" style="padding:20px; text-align:center; color:#64748b;">Consultando usuarios en base de datos...</td></tr>
          </tbody>
        </table>
      </div>
    `;

    // Listeners del form
    const formPanel = area.querySelector('#new-user-form-panel') as HTMLElement;
    area.querySelector('#btn-create-user')?.addEventListener('click', () => {
      formPanel.style.display = formPanel.style.display === 'none' ? 'block' : 'none';
    });
    area.querySelector('#btn-cancel-new-user')?.addEventListener('click', () => {
      formPanel.style.display = 'none';
    });

    area.querySelector('#btn-save-new-user')?.addEventListener('click', async () => {
      const username = (area.querySelector('#new-user-username') as HTMLInputElement).value.trim();
      const fullName = (area.querySelector('#new-user-fullname') as HTMLInputElement).value.trim();
      const badgeNumber = (area.querySelector('#new-user-badge') as HTMLInputElement).value.trim();
      const role = (area.querySelector('#new-user-role') as HTMLSelectElement).value;
      const password = (area.querySelector('#new-user-password') as HTMLInputElement).value;

      if (!username || !fullName || !password) {
        alert('Complete todos los campos obligatorios.');
        return;
      }

      try {
        const res = await fetch(`${this.apiBaseUrl}/api/v1/auth/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, fullName, badgeNumber, role, password })
        });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.message || 'Error guardando usuario');

        alert('Usuario creado exitosamente.');
        await this.loadUsersTab();
      } catch (err: any) {
        alert(`Error: ${err.message}`);
      }
    });

    // Cargar usuarios desde la API
    try {
      const res = await fetch(`${this.apiBaseUrl}/api/v1/auth/users`);
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        this.users = data.data;
        this.renderUsersRows();
      }
    } catch (e) {
      const tbody = area.querySelector('#admin-users-table-body');
      if (tbody) tbody.innerHTML = `<tr><td colspan="6" style="padding:20px; text-align:center; color:#ef4444;">Error conectando con la API de usuarios.</td></tr>`;
    }
  }

  private renderUsersRows(): void {
    const tbody = this.element?.querySelector('#admin-users-table-body');
    if (!tbody) return;

    if (this.users.length === 0) {
      tbody.innerHTML = `<tr><td colspan="6" style="padding:20px; text-align:center; color:#64748b;">No hay usuarios registrados.</td></tr>`;
      return;
    }

    tbody.innerHTML = this.users.map(u => {
      const roleColor = u.role === 'ADMIN' ? '#fbbf24' : (u.role === 'SUPERVISOR' ? '#38bdf8' : '#34d399');
      return `
        <tr style="border-bottom: 1px solid #1e293b;">
          <td style="padding:12px 14px; font-weight:700; color:#fff;">@${u.username}</td>
          <td style="padding:12px 14px; color:#cbd5e1;">${u.fullName}</td>
          <td style="padding:12px 14px; color:#94a3b8; font-family:monospace;">${u.badgeNumber || '---'}</td>
          <td style="padding:12px 14px;">
            <span style="background:rgba(255,255,255,0.08); padding:3px 8px; border-radius:4px; font-weight:700; font-size:0.75rem; color:${roleColor};">
              ${u.role}
            </span>
          </td>
          <td style="padding:12px 14px;">
            <span style="color:${u.active ? '#10b981' : '#ef4444'}; font-weight:600; font-size:0.8rem;">
              ● ${u.active ? 'Activo' : 'Inactivo'}
            </span>
          </td>
          <td style="padding:12px 14px; text-align:right;">
            <button class="btn-toggle-status" data-id="${u.id}" data-active="${u.active}" style="background:transparent; border:1px solid #475569; color:#cbd5e1; border-radius:4px; padding:4px 8px; font-size:0.75rem; cursor:pointer;">
              ${u.active ? 'Desactivar' : 'Activar'}
            </button>
          </td>
        </tr>
      `;
    }).join('');

    // Listener para cambiar estado activo/inactivo
    tbody.querySelectorAll('.btn-toggle-status').forEach(btn => {
      btn.addEventListener('click', async () => {
        const userId = btn.getAttribute('data-id');
        const currentActive = btn.getAttribute('data-active') === 'true';
        try {
          const res = await fetch(`${this.apiBaseUrl}/api/v1/auth/users/${userId}/status`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ active: !currentActive })
          });
          if (res.ok) {
            await this.loadUsersTab();
          }
        } catch (e) {
          alert('Error actualizando estado del usuario.');
        }
      });
    });
  }

  private async loadStorageTab(): Promise<void> {
    const area = this.element?.querySelector('#admin-content-area');
    if (!area) return;

    try {
      const res = await fetch(`${this.apiBaseUrl}/api/v1/media/retention-policy`);
      const data = await res.json();
      if (data.success) {
        this.retentionPolicy = data.data;
      }
    } catch (_) {}

    area.innerHTML = `
      <div style="background:#1e293b; border:1px solid #334155; border-radius:12px; padding:20px; max-width:650px;">
        <h3 style="margin:0 0 8px; font-size:1.1rem; color:#fff;">Política de Retención y Depuración de Grabaciones</h3>
        <p style="margin:0 0 16px; font-size:0.82rem; color:#94a3b8;">
          Define los días de resguardo legal y médico de los archivos de video comprimidos en disco antes de su purga automática.
        </p>

        <div style="display:flex; flex-direction:column; gap:16px;">
          <div>
            <label style="font-size:0.82rem; font-weight:700; color:#cbd5e1; display:block; margin-bottom:6px;">
              Días de Retención Obligatoria (NG911 / HIPAA / NOM)
            </label>
            <div style="display:flex; align-items:center; gap:8px;">
              <input type="number" id="inp-admin-retention-days" value="${this.retentionPolicy.retentionDays}" min="1" max="1825" class="form-input" style="width:120px;" />
              <span style="font-size:0.85rem; color:#94a3b8;">días</span>
            </div>
          </div>

          <div style="display:flex; align-items:center; gap:8px;">
            <input type="checkbox" id="chk-admin-autopurge" ${this.retentionPolicy.autoPurgeEnabled ? 'checked' : ''} style="width:18px; height:18px;" />
            <label for="chk-admin-autopurge" style="font-size:0.85rem; color:#cbd5e1; cursor:pointer;">
              Habilitar cron job de depuración física automática a las 03:00 hrs
            </label>
          </div>

          <div style="display:flex; gap:10px; margin-top:8px;">
            <button id="btn-admin-save-retention" style="background:#0284c7; color:#fff; border:none; padding:10px 18px; border-radius:6px; font-weight:700; cursor:pointer;">
              Guardar Configuración de Retención
            </button>
            <button id="btn-admin-purge-now" style="background:#ef4444; color:#fff; border:none; padding:10px 18px; border-radius:6px; font-weight:700; cursor:pointer;">
              🗑️ Ejecutar Purga Manual Ahora
            </button>
          </div>
        </div>
      </div>
    `;

    area.querySelector('#btn-admin-save-retention')?.addEventListener('click', async () => {
      const days = parseInt((area.querySelector('#inp-admin-retention-days') as HTMLInputElement).value, 10);
      const autoPurge = (area.querySelector('#chk-admin-autopurge') as HTMLInputElement).checked;

      try {
        const res = await fetch(`${this.apiBaseUrl}/api/v1/media/retention-policy`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ retentionDays: days, autoPurgeEnabled: autoPurge })
        });
        if (res.ok) alert('Política de retención actualizada.');
      } catch (e) {
        alert('Error al guardar la política.');
      }
    });

    area.querySelector('#btn-admin-purge-now')?.addEventListener('click', async () => {
      if (!confirm('¿Está seguro de forzar la purga manual de grabaciones vencidas ahora?')) return;
      try {
        const res = await fetch(`${this.apiBaseUrl}/api/v1/media/retention-policy/purge-now`, { method: 'POST' });
        const data = await res.json();
        alert(data.message || 'Purga ejecutada.');
      } catch (e) {
        alert('Error ejecutando purga.');
      }
    });
  }

  private async loadSystemTab(): Promise<void> {
    const area = this.element?.querySelector('#admin-content-area');
    if (!area) return;

    let metrics: any = {};
    try {
      const res = await fetch(`${this.apiBaseUrl}/api/v1/media/metrics`);
      const data = await res.json();
      if (data.success) metrics = data.data;
    } catch (_) {}

    area.innerHTML = `
      <div style="display:grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap:16px;">
        <div style="background:#1e293b; border:1px solid #334155; border-radius:10px; padding:16px;">
          <h4 style="margin:0 0 10px; font-size:0.85rem; color:#38bdf8; text-transform:uppercase;">Base de Datos PostgreSQL</h4>
          <div style="font-size:0.85rem; color:#cbd5e1; display:flex; flex-direction:column; gap:6px;">
            <div>Host: <strong>51.79.49.78:5432</strong></div>
            <div>Base: <strong>SOS_VideoCall</strong></div>
            <div>Tablas: <code>call_sessions</code>, <code>system_users</code>, <code>session_recordings</code></div>
            <div>Estado: <span style="color:#10b981; font-weight:700;">● CONECTADO</span></div>
          </div>
        </div>

        <div style="background:#1e293b; border:1px solid #334155; border-radius:10px; padding:16px;">
          <h4 style="margin:0 0 10px; font-size:0.85rem; color:#38bdf8; text-transform:uppercase;">Almacenamiento de Video</h4>
          <div style="font-size:0.85rem; color:#cbd5e1; display:flex; flex-direction:column; gap:6px;">
            <div>Espacio Ocupado: <strong>${metrics.totalStorageMb || '0.00'} MB</strong></div>
            <div>Archivos Forenses: <strong>${metrics.totalRecordings || 0} grabaciones</strong></div>
            <div>Códec: <strong>H.264 / AAC (libx264/openh264)</strong></div>
          </div>
        </div>

        <div style="background:#1e293b; border:1px solid #334155; border-radius:10px; padding:16px;">
          <h4 style="margin:0 0 10px; font-size:0.85rem; color:#38bdf8; text-transform:uppercase;">Proveedor de Enlaces</h4>
          <div style="font-size:0.85rem; color:#cbd5e1; display:flex; flex-direction:column; gap:6px;">
            <div>Servicio: <strong>Evolution API (WhatsApp / SMS)</strong></div>
            <div>Instancia: <strong>SOS Tech Ags</strong></div>
            <div>Endpoint: <code>https://mensajes.sostechags.xyz</code></div>
            <div>Estado: <span style="color:#10b981; font-weight:700;">● ACTIVO</span></div>
          </div>
        </div>
      </div>
    `;
  }

  public close(): void {
    if (this.element) {
      this.element.remove();
      this.element = null;
    }
  }
}
